import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Paper, CircularProgress, Alert, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import supabase from '../../../../../utils/supabase';
import DeleteIcon from '@mui/icons-material/Delete';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import VisibilityIcon from '@mui/icons-material/Visibility';

export default function AdminTeamMessages() {
  const { t } = useTranslation('adminTeam');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('contact_submissions')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching messages:', error);
      setError(t('messages.fetchError'));
    } else {
      setMessages(data || []);
      setError(null);
    }
    setLoading(false);
  }, [t]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleDelete = async (id) => {
    if (window.confirm(t('messages.deleteConfirm'))) {
      const { error } = await supabase.from('contact_submissions').delete().match({ id });
      if (error) {
        console.error('Error deleting message:', error);
        // You could show a snackbar here for better UX
      } else {
        setMessages((prev) => prev.filter((msg) => msg.id !== id));
      }
    }
  };

  const handleToggleRead = async (id, currentStatus) => {
    const { data, error } = await supabase
      .from('contact_submissions')
      .update({ is_read: !currentStatus })
      .match({ id })
      .select()
      .single();

    if (error) {
      console.error('Error updating status:', error);
    } else {
      setMessages((prev) => prev.map((msg) => (msg.id === id ? data : msg)));
    }
  };

  const handleViewDetails = (message) => {
    setSelectedMessage(message);
    // If the message is unread, mark it as read upon viewing
    if (!message.is_read) {
      handleToggleRead(message.id, false);
    }
  };

  const handleCloseDetails = () => setSelectedMessage(null);

  const columns = [
    {
      field: 'is_read',
      headerName: t('messages.read'),
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (params.value ? '✅' : '✉️'),
    },
    {
      field: 'created_at',
      headerName: t('messages.received'),
      width: 180,
      valueFormatter: ({ value }) => new Date(value).toLocaleString(),
    },
    { field: 'name', headerName: t('messages.name'), width: 150 },
    { field: 'subject', headerName: t('messages.subject'), flex: 1 },
    {
      field: 'actions',
      headerName: t('messages.actions'),
      width: 150,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <>
          <Tooltip title={t('messages.viewDetails')}>
            <IconButton onClick={() => handleViewDetails(params.row)}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.is_read ? 'Mark as Unread' : 'Mark as Read'}>
            <IconButton onClick={() => handleToggleRead(params.row.id, params.row.is_read)}>
              {params.row.is_read ? <MarkEmailUnreadIcon /> : <MarkEmailReadIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton onClick={() => handleDelete(params.row.id)} color="error">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h2" align="center" gutterBottom>
        {t('messages.title')}
      </Typography>
      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && (
        <Paper sx={{ height: '70vh', width: '100%' }}>
          <DataGrid
            rows={messages}
            columns={columns}
            pageSize={15}
            rowsPerPageOptions={[15, 30, 50]}
            getRowClassName={(params) => (params.row.is_read ? '' : 'font-bold')}
            components={{
              NoRowsOverlay: () => <Box sx={{p: 4, textAlign: 'center'}}>{t('messages.noMessages')}</Box>
            }}
          />
        </Paper>
      )}
      {selectedMessage && (
        <Dialog open={!!selectedMessage} onClose={handleCloseDetails} fullWidth maxWidth="md">
          <DialogTitle>{t('messages.detailsTitle')}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>{t('messages.from')}:</Typography>
                <Typography variant="body1">{selectedMessage.name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>{t('messages.email')}:</Typography>
                <Typography variant="body1">{selectedMessage.email}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>{t('messages.subject')}:</Typography>
                <Typography variant="body1">{selectedMessage.subject}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>{t('messages.message')}:</Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '40vh', overflowY: 'auto' }}>
                  <Typography variant="body2">
                    {selectedMessage.message}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetails}>
              {t('messages.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}